import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Camera, CircleHelp, ImagePlus } from "lucide-react";
import { CATALOG_URL, SITE_URL } from "../../config/site";
import SEO from "../../components/SEO";
import FramePhotoEditModal from "../../components/FramePhotoEditModal";
import FrameHowItWorksModal from "../../components/FrameHowItWorksModal";
import FrameRealGallery from "../../components/FrameRealGallery";
import FrameModelLinks from "../../components/FrameModelLinks";
import FrameProductVideo from "../../components/FrameProductVideo";
import { FRAMES, FRAME_TYPES } from "../../constants/frames";
import { useCart } from "../../cart/CartContext";
import "./FramesEditor.css";

import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";

const INDIVIDUAL_FRAMES_REAL_GALLERY = [
  {
    src: "/images/frame-gallery/frame-gallery-individuales/frame-gallery-individuales-nevera.webp",
    alt: "Tres marcos imantados individuales decorando una nevera",
  },
  {
    src: "/images/frame-gallery/frame-gallery-individuales/frame-gallery-individuales-escritorio.webp",
    alt: "Marco individual con soporte colocado sobre un escritorio",
  },
  {
    src: "/images/frame-gallery/frame-gallery-individuales/frame-gallery-individuales-taquilla.webp",
    alt: "Marco imantado individual colocado en una taquilla",
  },
];

export default function FramesEditor() {
  const { addItem } = useCart();
  const canvasRef = useRef(null);

  const isDraggingRef = useRef(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });

  const pinchDistanceRef = useRef(null);
  const pinchStartScaleRef = useRef(1);
  const touchTapRef = useRef(null);
  const mouseTapRef = useRef(null);
  const photoHintTimerRef = useRef(null);

  const [image, setImage] = useState(null);
  const [frameImage, setFrameImage] = useState(null);

  const [selectedFrame, setSelectedFrame] = useState(FRAMES[0]);
  const [selectedVariant, setSelectedVariant] = useState(FRAMES[0].variants[0]);
  const [mobileModelOpen, setMobileModelOpen] = useState(false);
  const [isMobileEditor, setIsMobileEditor] = useState(false);
  const [touchEditing, setTouchEditing] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoHintMessage, setPhotoHintMessage] = useState("");
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);

  const [quantity, setQuantity] = useState(1);
  const [frameProducts, setFrameProducts] = useState([]);
  const [checkoutMessage, setCheckoutMessage] = useState("");

  const [resetCount, setResetCount] = useState(0);

  const frameVariant = selectedVariant;
  const frameConfig = FRAME_TYPES[selectedFrame.type];
  const selectedColor = selectedVariant.id.replace(`${selectedFrame.id}-`, "");

  const selectedFrameProduct = frameProducts.find(
    (product) => product.id === selectedFrame.id,
  );

  const framePrice = selectedFrameProduct?.price || 0;
  const frameQuantities = selectedFrameProduct?.quantities || [1, 2, 3, 4];
  const canEditTouch = !isMobileEditor || touchEditing;
  const desktopPhotoBoxStyle = {
    left: `${((frameConfig.photoBox.x - 7) / frameConfig.canvas.w) * 100}%`,
    top: `${((frameConfig.photoBox.y - 1) / frameConfig.canvas.h) * 100}%`,
    width: `${((frameConfig.photoBox.w - 33) / frameConfig.canvas.w) * 100}%`,
    height: `${(frameConfig.photoBox.h / frameConfig.canvas.h) * 100}%`,
  };

  const mobilePhotoBoxStyle = {
    left: `${((frameConfig.photoBox.x +30) / frameConfig.canvas.w) * 100}%`,
    top: `${((frameConfig.photoBox.y + 30) / frameConfig.canvas.h) * 100}%`,
    width: `${((frameConfig.photoBox.w - 50) / frameConfig.canvas.w) * 100}%`,
    height: `${((frameConfig.photoBox.w - 40) / frameConfig.canvas.w) * 100}%`,
  };

  const photoBoxStyle = isMobileEditor ? mobilePhotoBoxStyle : desktopPhotoBoxStyle;

  const getCanvasPoint = (touch) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    return {
      x: ((touch.clientX - rect.left) / rect.width) * canvas.width,
      y: ((touch.clientY - rect.top) / rect.height) * canvas.height,
      clientX: touch.clientX,
      clientY: touch.clientY,
    };
  };

  const isPointInsidePhoto = (point) => {
    const box = frameConfig.photoBox;

    return (
      point.x >= box.x &&
      point.x <= box.x + box.w &&
      point.y >= box.y &&
      point.y <= box.y + box.h
    );
  };

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;

    return Math.sqrt(dx * dx + dy * dy);
  };

  const clampScale = (value) => {
    return Math.min(Math.max(value, 1), 4);
  };

  const clampPosition = (x, y, drawWidth, drawHeight, box) => {
    const limitX = Math.max(0, (drawWidth - box.w) / 2);
    const limitY = Math.max(0, (drawHeight - box.h) / 2);

    return {
      x: Math.min(Math.max(x, -limitX), limitX),
      y: Math.min(Math.max(y, -limitY), limitY),
    };
  };

  const resetImage = () => {
    isDraggingRef.current = false;
    pinchDistanceRef.current = null;
    pinchStartScaleRef.current = 1;

    setPosition({ x: 0, y: 0 });
    setScale(1);
    setResetCount((prev) => prev + 1);
  };

  const loadImageFile = (file) => {
    if (!file) return;

    const img = new Image();

    img.onload = () => {
      setImage(img);
      resetImage();
      setPhotoHintMessage("Foto añadida. Tócala para ajustarla.");
      window.clearTimeout(photoHintTimerRef.current);
      photoHintTimerRef.current = window.setTimeout(() => {
        setPhotoHintMessage("");
      }, 3000);
    };

    img.src = URL.createObjectURL(file);
  };

  const handleUpload = (e) => {
    loadImageFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const clearImage = () => {
    setImage(null);
    setPhotoModalOpen(false);
    setTouchEditing(false);
    setPhotoHintMessage("");
    touchTapRef.current = null;
    window.clearTimeout(photoHintTimerRef.current);
    resetImage();
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 980px)");

    const updateMobileEditor = () => {
      setIsMobileEditor(mediaQuery.matches);

      if (!mediaQuery.matches) {
        setTouchEditing(false);
        setPhotoModalOpen(false);
      }
    };

    updateMobileEditor();
    mediaQuery.addEventListener("change", updateMobileEditor);

    return () => {
      mediaQuery.removeEventListener("change", updateMobileEditor);
    };
  }, []);

  useEffect(() => {
    return () => {
      window.clearTimeout(photoHintTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const frame = new Image();

    frame.onload = () => {
      setFrameImage(frame);
    };

    frame.src = frameVariant.frameSrc;
  }, [frameVariant.frameSrc]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetch(CATALOG_URL);

        const data = await response.json();

        const frames = data.products.filter(
          (product) => product.category === "frames" && product.active,
        );

        setFrameProducts(frames);
      } catch (error) {
        console.error("Error loading frame products", error);
      }
    };

    loadProducts();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wheelHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const zoomSpeed = 0.1;
      const delta = e.deltaY > 0 ? -zoomSpeed : zoomSpeed;

      setScale((prev) => clampScale(prev + delta));
    };

    canvas.addEventListener("wheel", wheelHandler, {
      passive: false,
    });

    return () => {
      canvas.removeEventListener("wheel", wheelHandler);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !frameImage) return;

    const ctx = canvas.getContext("2d");

    canvas.width = frameConfig.canvas.w;
    canvas.height = frameConfig.canvas.h;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(frameImage, 0, 0, canvas.width, canvas.height);

    if (!image) return;

    const box = frameConfig.photoBox;

    const imgRatio = image.width / image.height;
    const boxRatio = box.w / box.h;

    let drawWidth;
    let drawHeight;

    if (imgRatio > boxRatio) {
      drawHeight = box.h;
      drawWidth = drawHeight * imgRatio;
    } else {
      drawWidth = box.w;
      drawHeight = drawWidth / imgRatio;
    }

    drawWidth = drawWidth * scale;
    drawHeight = drawHeight * scale;

    const offsetX = (box.w - drawWidth) / 2;
    const offsetY = (box.h - drawHeight) / 2;

    const limitedPosition = clampPosition(
      position.x,
      position.y,
      drawWidth,
      drawHeight,
      box,
    );

    ctx.save();

    ctx.beginPath();
    ctx.rect(box.x, box.y, box.w, box.h);
    ctx.clip();

    ctx.drawImage(
      image,
      box.x + offsetX + limitedPosition.x,
      box.y + offsetY + limitedPosition.y,
      drawWidth,
      drawHeight,
    );

    ctx.restore();
  }, [image, frameImage, frameConfig, position, scale, resetCount]);

  const handleMouseDown = (e) => {
    if (!image || !canEditTouch) return;

    const point = getCanvasPoint(e);
    mouseTapRef.current = {
      startsInsidePhoto: isPointInsidePhoto(point),
      startX: point.clientX,
      startY: point.clientY,
      moved: false,
    };

    isDraggingRef.current = true;

    lastMouseRef.current = {
      x: e.clientX,
      y: e.clientY,
    };
  };

  const handleMouseMove = (e) => {
    if (!canEditTouch || !isDraggingRef.current) return;

    if (mouseTapRef.current) {
      const tapDx = e.clientX - mouseTapRef.current.startX;
      const tapDy = e.clientY - mouseTapRef.current.startY;

      if (Math.sqrt(tapDx * tapDx + tapDy * tapDy) > 12) {
        mouseTapRef.current.moved = true;
      }
    }

    const dx = e.clientX - lastMouseRef.current.x;
    const dy = e.clientY - lastMouseRef.current.y;

    setPosition((prev) => ({
      x: prev.x + dx,
      y: prev.y + dy,
    }));

    lastMouseRef.current = {
      x: e.clientX,
      y: e.clientY,
    };
  };

  const handleTouchStart = (e) => {
    const firstTouch = e.touches[0];

    if (isMobileEditor && image && firstTouch) {
      const point = getCanvasPoint(firstTouch);

      touchTapRef.current = {
        startsInsidePhoto: isPointInsidePhoto(point),
        startX: point.clientX,
        startY: point.clientY,
        moved: false,
      };
    }

    if (!image || !canEditTouch) {
      stopDragging();
      return;
    }

    if (e.touches.length === 1) {
      isDraggingRef.current = true;

      lastMouseRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }

    if (e.touches.length === 2) {
      isDraggingRef.current = false;
      pinchDistanceRef.current = getTouchDistance(e.touches);
      pinchStartScaleRef.current = scale;
    }
  };

  const handleTouchMove = (e) => {
    if (touchTapRef.current && e.touches[0]) {
      const touch = e.touches[0];
      const dx = touch.clientX - touchTapRef.current.startX;
      const dy = touch.clientY - touchTapRef.current.startY;

      if (Math.sqrt(dx * dx + dy * dy) > 12) {
        touchTapRef.current.moved = true;
      }
    }

    if (!image || !canEditTouch) {
      stopDragging();
      return;
    }

    if (e.touches.length === 1 && isDraggingRef.current) {
      const touch = e.touches[0];

      const dx = touch.clientX - lastMouseRef.current.x;
      const dy = touch.clientY - lastMouseRef.current.y;

      setPosition((prev) => ({
        x: prev.x + dx,
        y: prev.y + dy,
      }));

      lastMouseRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
    }

    if (e.touches.length === 2 && pinchDistanceRef.current) {
      const currentDistance = getTouchDistance(e.touches);
      const zoomFactor = currentDistance / pinchDistanceRef.current;
      const nextScale = pinchStartScaleRef.current * zoomFactor;

      setScale(clampScale(nextScale));
    }
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
    pinchDistanceRef.current = null;
  };
  const handleMouseUp = () => {
    const tap = mouseTapRef.current;
    mouseTapRef.current = null;
    stopDragging();

    if (image && tap?.startsInsidePhoto && !tap.moved) {
      setPhotoModalOpen(true);
    }
  };

  const handleTouchEnd = () => {
    const tap = touchTapRef.current;
    touchTapRef.current = null;
    stopDragging();

    if (
      isMobileEditor &&
      image &&
      tap?.startsInsidePhoto &&
      !tap.moved
    ) {
      setPhotoModalOpen(true);
    }
  };

  const handleTouchCancel = () => {
    touchTapRef.current = null;
    stopDragging();
  };

  const handleAddFrameToCart = () => {
    addItem({
      productId: selectedFrame.id,
      productName: selectedFrameProduct?.name || selectedFrame.name,
      category: selectedFrameProduct?.category || "frames",
      type: selectedFrameProduct?.type || selectedFrame.type,
      quantity,
      unitPrice: framePrice,
      currency: selectedFrameProduct?.currency || "EUR",
      options: {
        color: {
          label: "Color",
          value: selectedColor,
          valueLabel: selectedVariant.name,
        },
      },
      assets: [],
      notes: [
        "La foto subida en el editor de marcos es solo una vista previa y no forma parte del pedido.",
      ],
    });

    setCheckoutMessage("Marco anadido al carrito.");
  };

  return (
    <>
<SEO
  title="Marcos individuales para imanes personalizados | Imalandia"
  description="Prueba tus fotos en marcos individuales para imanes de 5x5 cm y elige modelo, color y soporte antes de añadirlo al carrito."
  canonical={`${SITE_URL}/marcos-individuales`}
/>
      <Header />

      <div className="frames-editor">
        <header className="frames-page-title">
          <h1>Marcos imantados personalizados</h1>
        </header>

        <div className="frames-preview-note">
          <span>
            La foto es solo una vista previa. El pedido de los marcos no incluye
            imanes personalizados.
          </span>
          <button
            type="button"
            className="frame-help-trigger"
            onClick={() => setHelpModalOpen(true)}
          >
            <CircleHelp aria-hidden="true" />
            Como funciona
          </button>
        </div>

        <aside className="frames-editor-left">
          <div className="frames-selector-block">
            <h2>Elige color</h2>

            <div className="variants-list">
              {selectedFrame.variants.map((variant) => (
                <button
                  key={variant.id}
                  type="button"
                  className={
                    selectedVariant.id === variant.id
                      ? "variant-button active"
                      : "variant-button"
                  }
                  title={variant.name}
                  onClick={() => setSelectedVariant(variant)}
                  style={{ background: variant.swatch }}
                />
              ))}
            </div>
          </div>

          <div className="frames-info-box frames-compact-link-box">
            <p>
              Si quieres recibir tus fotos impresas como imanes personalizados,
              puedes hacerlo desde la pagina de imanes.
            </p>

            <Link to="/fotos">Ver imanes personalizados</Link>
          </div>
        </aside>

        <main className="frames-editor-main">
          <div className="frames-mobile-three-column-editor">
            <div className="frames-mobile-color-rail" aria-label="Colores">
              {selectedFrame.variants.map((variant) => (
                <button
                  key={variant.id}
                  type="button"
                  className={
                    selectedVariant.id === variant.id
                      ? "frames-mobile-color active"
                      : "frames-mobile-color"
                  }
                  title={variant.name}
                  onClick={() => setSelectedVariant(variant)}
                  style={{ background: variant.swatch }}
                />
              ))}
            </div>

            <div className="frames-mobile-canvas-column">
                            <div className="frames-canvas-wrap">
                <canvas
                  ref={canvasRef}
                  className={canEditTouch ? "frames-canvas-editing" : "frames-canvas-scroll"}
                  role="img"
                  aria-label="Editor de vista previa para marcos imantados personalizados"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={stopDragging}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchCancel}
                />
                <label
                  className={
                    image
                      ? "frames-photo-drop-zone has-image"
                      : "frames-photo-drop-zone"
                  }
                  style={
                    image
                      ? {
                          left: `${((frameConfig.photoBox.x + frameConfig.photoBox.w - 150) / frameConfig.canvas.w) * 100}%`,
                          top: `${((frameConfig.photoBox.y + 8) / frameConfig.canvas.h) * 100}%`,
                          width: `${(92 / frameConfig.canvas.w) * 100}%`,
                          height: `${(92 / frameConfig.canvas.h) * 100}%`,
                        }
                      : photoBoxStyle
                  }
                  onClick={(event) => {
                    if (image) {
                      event.preventDefault();
                      setPhotoModalOpen(true);
                    }
                  }}
                >
                                    <span>
                    {image ? (
                      <Camera size={34} strokeWidth={2.5} />
                    ) : (
                      <>
                        <ImagePlus size={24} strokeWidth={2.4} />
                        <strong>Clic aqui</strong>
                        <small>Subir foto</small>
                      </>
                    )}
                  </span>
                  <input type="file" accept="image/*" onChange={handleUpload} />
                </label>
              </div>
              {isMobileEditor && image && (
                <p className="frames-photo-edit-hint" aria-live="polite">
                  {photoHintMessage || "Toca la foto para ajustar el encuadre."}
                </p>
              )}

              <div
                className={
                  isMobileEditor && image
                    ? "frames-editor-actions with-touch-edit"
                    : "frames-editor-actions"
                }
              >
                {isMobileEditor && image && (
                  <button
                    type="button"
                    className={
                      touchEditing
                        ? "frames-touch-edit-button active"
                        : "frames-touch-edit-button"
                    }
                    onClick={() => {
                      stopDragging();
                      setTouchEditing((prev) => !prev);
                    }}
                  >
                    {touchEditing ? "Listo" : "Mover foto"}
                  </button>
                )}
              </div>
            </div>

            <div className="frames-mobile-model-rail" aria-label="Modelos">
              {FRAMES.map((frame) => {
                const previewVariant =
                  frame.variants.find((variant) =>
                    variant.id.endsWith(`-${selectedColor}`)
                  ) || frame.variants[0];

                return (
                  <button
                    key={frame.id}
                    type="button"
                    className={
                      selectedFrame.id === frame.id
                        ? "frames-mobile-model-thumb active"
                        : "frames-mobile-model-thumb"
                    }
                    title={frame.name}
                    aria-label={frame.name}
                    onClick={() => {
                      setSelectedFrame(frame);
                      setSelectedVariant(previewVariant);
                      setMobileModelOpen(false);
                      resetImage();
                    }}
                  >
                    <img src={previewVariant.frameSrc} alt="" />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="frames-photo-reset-row">
            <button
              type="button"
              className="frames-clear-photo-button"
              onClick={clearImage}
              disabled={!image}
            >
              Borrar fotos
            </button>
          </div>

        <section className="frames-editor-bottom">
          <div className="frames-order-card">
            <div className="frames-order-header">
              <h2>Pedido</h2>
            </div>

            <div className="frames-purchase-row">
              <div className="frames-order-options">
                <select
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                >
                  {frameQuantities.map((qty) => (
                    <option key={qty} value={qty}>
                      {qty} unidad{qty > 1 ? "es" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="frames-price-box">
                <span>{framePrice.toFixed(2)} EUR / unidad</span>

                <strong>Total: {(framePrice * quantity).toFixed(2)} EUR</strong>
              </div>
            </div>

            {checkoutMessage && (
              <p className="frames-checkout-message">{checkoutMessage}</p>
            )}

            <div className="frames-order-actions">
              <button
                type="button"
                className="continue-button"
                onClick={handleAddFrameToCart}
              >
                Anadir al carrito
              </button>

              <Link to="/carrito" className="frames-cart-link">
                Ver carrito
              </Link>
            </div>
          </div>
        </section>

        </main>

        <aside className="frames-editor-right">
          <div className="frames-selector-block">
            <h2>Elige modelo</h2>

<div className="frames-mobile-model-picker">
  <button
    type="button"
    className="frames-mobile-model-trigger"
    onClick={() => setMobileModelOpen((prev) => !prev)}
  >
    <img
      src={selectedVariant.frameSrc}
      alt=""
    />

    <span>{selectedFrame.name}</span>

    <strong>{mobileModelOpen ? "â–²" : "â–¼"}</strong>
  </button>

  {mobileModelOpen && (
    <div className="frames-mobile-model-options">
      {FRAMES.map((frame) => {
        const previewVariant = frame.variants[0];

        return (
          <button
            key={frame.id}
            type="button"
            className={
              selectedFrame.id === frame.id
                ? "frames-mobile-model-option active"
                : "frames-mobile-model-option"
            }
            onClick={() => {
              setSelectedFrame(frame);
              setSelectedVariant(frame.variants[0]);
              setMobileModelOpen(false);
              resetImage();
            }}
          >
            <img
              src={previewVariant.frameSrc}
              alt=""
            />

            <span>{frame.name}</span>
          </button>
        );
      })}
    </div>
  )}
</div>

            <div className="frames-list frames-model-icons">
              {FRAMES.map((frame) => {
                const previewVariant =
                  frame.variants.find((variant) =>
                    variant.id.endsWith(`-${selectedColor}`)
                  ) || frame.variants[0];

                return (
                  <button
                    key={frame.id}
                    type="button"
                    className={selectedFrame.id === frame.id ? "active" : ""}
                    title={frame.name}
                    aria-label={frame.name}
                    onClick={() => {
                      setSelectedFrame(frame);
                      setSelectedVariant(previewVariant);
                      resetImage();
                    }}
                  >
                    <img src={previewVariant.frameSrc} alt="" />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="frames-support-info frames-compact-link-box">
            <h2>Quieres colocarlo fuera de la nevera?</h2>

            <p>
              Puedes colocarlo en estanterias, escritorios o muebles usando
              nuestro soporte opcional.
            </p>

            <Link to="/producto/soporte-marcos-imanes">Ver soporte opcional</Link>
          </div>
        </aside>

        <div className="frames-real-gallery">
          <FrameRealGallery
            images={INDIVIDUAL_FRAMES_REAL_GALLERY}
            title="Un marco, mil rincones"
            description="En la nevera, en tu escritorio o hasta en la taquilla. Tus recuerdos siempre encuentran su sitio."
          />
        </div>
        <FrameProductVideo
          videoSrc="/videos/marcos-individuales.mp4"
          title="Marcos individuales en accion"
          description="Modelos, colores y soportes para imaginar tus fotos en cualquier rincon."
          label="Video de marcos individuales personalizados"
          wide
        />
        <FrameModelLinks currentModel="individuales" />

      </div>

      {photoModalOpen && image && (
        <FramePhotoEditModal
          box={frameConfig.photoBox}
          slot={{ image, position, scale }}
          title="Ajustar foto"
          compactOnMobile
          onClose={() => setPhotoModalOpen(false)}
          onConfirm={({ position: nextPosition, scale: nextScale }) => {
            setPosition(nextPosition);
            setScale(nextScale);
            setPhotoModalOpen(false);
          }}
          onChangeImage={loadImageFile}
        />
      )}

      {helpModalOpen && (
        <FrameHowItWorksModal
          firstStepTitle="Elige el modelo y el color"
          firstStepText="Combina los modelos y colores disponibles hasta encontrar tu favorito."
          onClose={() => setHelpModalOpen(false)}
        />
      )}

      <Footer />
    </>
  );
}






















